import { Injectable } from '@angular/core';
import { Observable, Subject, throwError } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { HttpHeadersService } from '../../core/shared-service/http-headers.service';
import {
  facilityLevelObject,
  facilityNodeObject,
  IMultiFacility,
  InvitedFacility,
  InvitedFacilityResponse,
  MultiFacilityModel,
  PayloadSetLevelFacilityTrees,
  PayloadSetLevelFacilityTreesFacilityNodes,
  RequestInviteFacilityModel,
  ResponseInviteFacilityModel,
  SelectWithCheckboxRadioOption,
  SelectWithCheckboxRadioValue,
  AcceptRejectRequestPayload,
} from './multi-facility.model';
import { FacilitySummaryObject, IHeatMapDash } from '../../core/services/facility.model';
import { FacilityService } from 'src/app/core/services/facility.service';
@Injectable({
  providedIn: 'root',
})
export class MultiFacilityService {
  private _multiFacilities: MultiFacilityModel[] = [];
  private _multiFacilitiesFlat: MultiFacilityModel[] = [];
  private _manageFacilityHint: boolean = true;
  private _allowedLevel: number = 5;
  private _setLevelHint: boolean = true;
  private _highlightNode: string = '';
  private _facilityMap: Map<String, FacilitySummaryObject>;
  private _multifacilityUrls: string[];
  private invitedFacilitiesSbj: Subject<InvitedFacilityResponse> = new Subject();

  private _setLevelUpdated$ = new Subject<boolean|null>();
  public setLevelUpdated$ = this._setLevelUpdated$.asObservable();
  private _selectedFacilityListSource = new Subject<string[]>();
  public selectedFacilityListSource = this._selectedFacilityListSource.asObservable();
  public invitedFacilities$ = this.invitedFacilitiesSbj.asObservable();
  
  constructor(private httpClient: HttpClient, 
    private httpHeaderService: HttpHeadersService,
    private _facilityService: FacilityService) {
    this._setLevelUpdated$.next(false);
  }

  public setMultiFacilityLevel(postData: PayloadSetLevelFacilityTrees): Observable<IMultiFacility> {
    const headers = this.httpHeaderService.bearerHttpAuthorizationHeader();
    const result = this.httpClient.post(environment.servicePath + '/multi_facility/set_level', postData, { headers });
    return result as Observable<IMultiFacility>;
  }

  public getFacilityDashboardData(facId: string): Observable<IHeatMapDash> {
    const headers = this.httpHeaderService.bearerHttpAuthorizationHeader();
    const result = this.httpClient.get(environment.servicePath + '/heatmap/dashboard?facilityId=' + facId, { headers });
    return result as Observable<IHeatMapDash>;
  }

  public saveFacilityUserPreference(postData: any): Observable<any> {
    const headers = this.httpHeaderService.bearerHttpAuthorizationHeader();
    return this.httpClient.post(environment.servicePath + '/lfasset_facility/facility_user_preference', postData, {
      headers,
    });
  }

  resetMultiFacilitiesList() {
    this._multiFacilities.length = 0;
  }

  getMultiFacilities(): Promise<MultiFacilityModel[]> {
    return new Promise((resolve, reject) => {
      this._facilityMap = this._facilityService.allFacilities();
      const headers = this.httpHeaderService.bearerHttpAuthorizationHeader();
      this.httpClient.get(environment.servicePath + '/multi_facility', { headers }).subscribe(
        (res: IMultiFacility) => {

          //Reorder tree. Below is the reorderng of the tree node if they don't have access to see the parent level.
          res.facilityTrees.forEach(tree => {
            let tmp = tree.facilityNodes.sort((a, b) => (Number(a.level) > Number(b.level)) ? 1 : -1);
            if(tmp[0].level !== '1'){
              let levelAdjustment = Number(tmp[0].level)-1;
              tmp.forEach(childTree => {
                childTree.level = String(Number(childTree.level)-levelAdjustment);
              })
            }
            tree.facilityNodes = tmp;
            return tree;
          });
          
          this._multiFacilitiesFlat = [];
          this._multiFacilities = [];
          this.manageFacilityHint = res.manageFacilityHint === 'true';
          this.setLevelHint = res.setLevelHint === 'true';
          this.allowedLevel = 5;
          res.facilityTrees?.forEach((node: facilityNodeObject) => {
            if (node['facilityNodes'] && node['facilityNodes'].length > 0) {
              //added condition on below lines of code since at times child node is there but not parent node
              let treeData = this._toTree(node['facilityNodes'], null);
              if (treeData) {
                this._multiFacilities.push(treeData);
              }
            }
          });

          resolve(this._multiFacilities);
        },
        (err) => reject()
      );
    });
  }

  isMultifacilityUrl(url: string | undefined) {
    if(!url) return false;
    return this._multifacilityUrls?.find(u => u === url) ? true : false;
  }

  setMultifacilityUrls(urls: string[]) {
    this._multifacilityUrls = urls;
  }

  //Below function is not responsible only for lfod supported facilities and also checking for isMultifacility, later we may need to refactor based on the some criteria.
  isMultifacilitySupported(url: string | undefined, facilityData?: MultiFacilityModel) {
    if(!url) return false;

    if(this.isMultifacilityUrl(url) || url === '/videoLibrary' || url === '/videoLibrary/customizeBranding/addLogo' || 
      url === '/videoLibrary/customizeBranding/banner'  || url === '/videoLibrary/customizeBranding/categoryDisplayName') {
      if(!facilityData) {
        let selectedFacility = this._facilityService?.selectedFacility();
        if(!selectedFacility) return false;
        facilityData = this._multiFacilitiesFlat.find(m => m.facilityId === selectedFacility.facilityId);
      } 

      return facilityData?.isLFODSupportedFacility;
    }
    
    return this.isMultifacilityUrl(url);
  }

  updateSelectedFacilities(facilities: string[]) {
    this._facilityService.updateSelectedFacilities(facilities);
    this._selectedFacilityListSource.next(facilities);
  }

  getAllFacilitiesLength(): number {
    return this._multiFacilitiesFlat.length;
  }

  resetMultiFacilitiesDetails(): void {
    this._multiFacilitiesFlat = [];
    this._multiFacilities = [];
  }

  get manageFacilityHint(): boolean {
    return this._manageFacilityHint;
  }

  set manageFacilityHint(value: boolean) {
    this._manageFacilityHint = value;
  }

  get setLevelHint(): boolean {
    return this._setLevelHint;
  }

  set setLevelHint(value: boolean) {
    this._setLevelHint = value;
  }

  get highlightNode(): string {
    return this._highlightNode;
  }

  set highlightNode(value: string) {
    this._highlightNode = value;
  }

  get allowedLevel(): number {
    return this._allowedLevel;
  }

  set allowedLevel(value: number) {
    this._allowedLevel = value;
  }

  private _toTree(data: facilityLevelObject[], item: any): MultiFacilityModel | null {
    data  = data.sort((a,b) => (a.level > b.level ? 1 : -1));
    if (!item) {
      item = data[0];
      if (item && !item.hasOwnProperty('path')) {
        item['path'] = item.facilityId;
      }
    }

    if (item) {
      let facMap = this._facilityMap.get(item['facilityId']);

      if (facMap) {
        if (!item.hasOwnProperty('facilityName')) {
          item['facilityName'] = facMap?.facilityName;
        }
        item['isAdmin'] = facMap.isLoggedInManagerAdmin === 'true';
        item['isLFODSupportedFacility'] = facMap.facilityRights.lfodEnabledEquipment === 'true';
      } else {
        if (item.soleManager === 'true') {
          item['isAdmin'] = true;
        } else {
          item['isAdmin'] = false;
        }
      }

      this._multiFacilitiesFlat.push(item);

      let parent = { ...item };
      parent.children = data
        .filter((x) => x.parentId === item.facilityId)
        // .sort((a, b) => parseInt(a.level, 10) - parseInt(b.level, 10))
        .map((y) => this._toTree(data, y));

      return parent;
    } else {
      return null;
    }
  }

  private _updateFlat(facId: string) {
    const updatedFlatObj = (facObj: facilityLevelObject, _facId: string): facilityLevelObject => {
      if (facObj.facilityId === _facId) {
        facObj.level = '1';
        facObj.path = _facId;
        delete facObj.parentId;
      } else {
        let _deleteIndex = facObj.path ? facObj.path?.indexOf(_facId) : -1;
        facObj.path = facObj.path?.substring(_deleteIndex, facObj.path.length);
        facObj.level = facObj.path ? facObj.path?.split('.').length.toString() : '1';
      }
      return facObj;
    };

    this._multiFacilitiesFlat.forEach((item) => {
      if (item.path && item.path.indexOf(facId) > -1) {
        updatedFlatObj(item, facId);
      }
    });
  }

  public getParents(selectedFacility: MultiFacilityModel): SelectWithCheckboxRadioOption[] {
    const dropdown: SelectWithCheckboxRadioOption[] = [];

    // ignore current and child
    // let _currentFlat = this._toFlat(selectedFacility);
    // ignore children nodes of currently selected facility
    let _ignoreFacilityIds: string[] = []; //_currentFlat.map(value => value.facilityId);
    if (selectedFacility.children.length > 0) {
      selectedFacility.children.forEach((item) => {
        _ignoreFacilityIds.push(item.facilityId);
      });
    }

    // ignore current and parent heirarchy facilities
    let _path = selectedFacility.path.split('.');
    _path.pop(); //ignore the current facility in ignoreFacilityIds array
    _ignoreFacilityIds.push(..._path);

    //TODO: need to check on level and then eliminate it from the list
    //getting the leaf nodes of heirarchy that is passed as parent_item and returning path of leaf nodes
    const getLeafNodesPath = (parent_item: MultiFacilityModel[], result_item: String[]): String[] => {
      parent_item.forEach((childNode) => {
        if (childNode.children.length) {
          getLeafNodesPath(childNode.children, result_item);
        } else {
          result_item.push(childNode.path);
        }

        //TODO: ignoring non admin facilities in heirarchy
        if (!childNode.isAdmin && !_ignoreFacilityIds.includes(childNode.facilityId)) {
          _ignoreFacilityIds.push(childNode.facilityId);
        }
        return result_item;
      });
      return result_item;
    };

    this._multiFacilities.forEach((facility) => {
      if (facility.path && facility.children.length && !_ignoreFacilityIds.includes(facility.facilityId)) {
        if (!facility.isAdmin && !_ignoreFacilityIds.includes(facility.facilityId)) {
          _ignoreFacilityIds.push(facility.facilityId);
        }
        //getting path of each heirarchy if it has children
        let leafNode_path = getLeafNodesPath(facility.children, []);
        let current_level = Number(selectedFacility.level);
        leafNode_path.forEach((path) => {
          let _selFacArr = path?.split('.');
          let _allowedItems = current_level + _selFacArr.length - this.allowedLevel;
          if (_allowedItems > 0) {
            _selFacArr.reverse().splice(0, _selFacArr.length - _allowedItems);
            let _uniqueArr = Array.from(new Set(_selFacArr));
            _ignoreFacilityIds.push(..._uniqueArr);
          }
        });
      } else {
        //ignoring independent facilities for which the user is not admin
        if (!facility.isAdmin && !_ignoreFacilityIds.includes(facility.facilityId)) {
          _ignoreFacilityIds.push(facility.facilityId);
        }
      }
    });

    //ignoring current facility
    if (!_ignoreFacilityIds.includes(selectedFacility.facilityId)) {
      _ignoreFacilityIds.push(selectedFacility.facilityId);
    }

    this._multiFacilitiesFlat.forEach((facility) => {
      if (!_ignoreFacilityIds.includes(facility.facilityId)) {
        dropdown.push(new SelectWithCheckboxRadioOption(facility.facilityId, facility.facilityName || '', false));
      }
    });
    return dropdown;
  }

  private _flatToTreeStructure(facId?: string) {
    this._multiFacilities.length = 0;

    const createTree = (facility_obj: facilityLevelObject[], parent_item: MultiFacilityModel): MultiFacilityModel => {
      facility_obj.forEach((childNode) => {
        let child_node = new MultiFacilityModel();
        if (childNode.parentId === parent_item.facilityId) {
          parent_item.children.push(Object.assign(child_node, childNode));
          if (facility_obj.filter((node) => node.parentId === child_node.parentId).length > 0) {
            createTree(facility_obj, child_node);
          }
        }
        return parent_item;
      });
      return parent_item;
    };

    this._multiFacilitiesFlat.forEach((facility) => {
      const getRelatedNodes = this._multiFacilitiesFlat.filter(
        (item) => item.path && item.path?.split('.').indexOf(facility.facilityId) > -1
      );

      if (getRelatedNodes.length === 1 && getRelatedNodes[0].level === '1') {
        let add_obj: facilityLevelObject = getRelatedNodes[0];

        let new_item = new MultiFacilityModel(add_obj.facilityId, add_obj?.facilityName, add_obj.level, add_obj.path);

        //updated facilityname and isAdmin if doesnt exist
        let facMap = this._facilityMap.get(facility['facilityId']);

        if (facMap) {
          if (!facility.hasOwnProperty('facilityName')) {
            new_item['facilityName'] = facMap?.facilityName;
          }
          new_item['isAdmin'] = facMap.isLoggedInManagerAdmin === 'true';
        } else {
          new_item['isAdmin'] = false;
        }

        //TODO: we need to check on soleManager property

        this._multiFacilities.push(new_item);
      } else if (getRelatedNodes.length > 1) {
        let parent_node: MultiFacilityModel;
        let top_parent = getRelatedNodes.find((it) => it.level === '1');
        if (top_parent) {
          parent_node = new MultiFacilityModel(
            top_parent.facilityId,
            top_parent?.facilityName,
            top_parent.level,
            top_parent.path,
            [],
            undefined,
            facId ? top_parent.facilityId === facId : false
          );

          //updated facilityname and isAdmin if doesnt exist
          let facMap = this._facilityMap.get(facility['facilityId']);

          if (facMap) {
            if (!facility.hasOwnProperty('facilityName')) {
              parent_node['facilityName'] = facMap?.facilityName;
            }
            parent_node['isAdmin'] = facMap.isLoggedInManagerAdmin === 'true';
          } else {
            parent_node['isAdmin'] = false;
          }

          //TODO: we need to check on soleManager property
          this._multiFacilities.push(createTree(getRelatedNodes, parent_node));
        }
      }
    });

    if (facId) {
      this.highlightNode = facId;
    }
  }

  public setIndependent(selectedFacility: MultiFacilityModel): any {
    let payload: PayloadSetLevelFacilityTrees = new PayloadSetLevelFacilityTrees([
      new PayloadSetLevelFacilityTreesFacilityNodes(selectedFacility.facilityId, null, selectedFacility.facilityId),
    ]);

    return new Promise((resolve, reject) => {
      this.setMultiFacilityLevel(payload).subscribe(
        (res) => {
          this._updateFlat(selectedFacility.facilityId);
          this._flatToTreeStructure(selectedFacility.facilityId);
          this._setLevelUpdated$.next(true);
          resolve(true);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }

  public setParent(
    selectedFacility: MultiFacilityModel,
    selectedSubsidiaryFacilities: SelectWithCheckboxRadioValue
  ): any {
    const _findRoot = (
      _facilities: MultiFacilityModel[],
      _id: string,
      _selectedTree: MultiFacilityModel[]
    ): MultiFacilityModel[] => {
      let f = _facilities.find((e) => e.facilityId === _id);
      if (f) {
        _selectedTree.push(f);
      } else {
        _facilities.forEach((_facility) => {
          if (_facility.children.length) {
            return _findRoot(_facility.children, _id, _selectedTree);
          }
          return true;
        });
      }
      return _selectedTree;
    };

    let jsonSelectedFacility = JSON.parse(JSON.stringify(selectedFacility));
    jsonSelectedFacility.children.length = 0; // deleting children node if any for selected parent facility since the backend needs just the parent and not the existing children
    selectedSubsidiaryFacilities.selected.length &&
      selectedSubsidiaryFacilities.selected.forEach((item) => {
        jsonSelectedFacility.children = jsonSelectedFacility.children.concat(
          _findRoot(this._multiFacilities, item.id, [])
        );
      });

    // flat
    const payloadGenerate = (
      _facilities: MultiFacilityModel[],
      _parent: string | null,
      _path: string | null,
      _level: string | null,
      _collection: PayloadSetLevelFacilityTreesFacilityNodes[]
    ) => {
      _facilities.forEach((item) => {
        let _pathNew = (_path ? _path + '.' : '') + item.facilityId;
        _collection.push(
          new PayloadSetLevelFacilityTreesFacilityNodes(
            item.facilityId,
            _parent,
            _pathNew,
            _pathNew.split('.').length.toString()
          )
        );
        if (item.children.length) {
          return payloadGenerate(
            item.children,
            item.facilityId,
            _pathNew,
            _pathNew.split('.').length.toString(),
            _collection
          );
        } else {
          return true;
        }
      });
      return _collection;
    };

    let _pathWithoutSelf = selectedFacility.path.split('.');
    _pathWithoutSelf.length = _pathWithoutSelf.length - 1;
    if (selectedFacility.level === '1') {
      delete selectedFacility['parentId'];
    }
    let payloads = payloadGenerate(
      [jsonSelectedFacility],
      selectedFacility?.parentId || null,
      _pathWithoutSelf.join('.'),
      selectedFacility.level,
      []
    );
    let payload: PayloadSetLevelFacilityTrees = new PayloadSetLevelFacilityTrees(payloads);

    return new Promise((resolve, reject) => {
      this.setMultiFacilityLevel(payload).subscribe(
        (res) => {
          this._setLevelUpdated$.next(true);
          resolve(true);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }

  public deLinkUser(facilityId: string): Observable<any> {
    const headers = this.httpHeaderService.bearerHttpAuthorizationHeader();
    let params = new HttpParams();
    params = params.set('facilityId', facilityId);
    return this.httpClient.post(environment.servicePath + '/multi_facility/delink-user', {}, { headers, params });
  }

  public async invitedFacilities() {
    const headers = this.httpHeaderService.bearerHttpAuthorizationHeader();
    let params = new HttpParams();
    params = params.set('status', 'PENDING').set('createdBy', 'true').set('approveBy', 'true');
    let data = await this.httpClient.get(environment.servicePath + '/multi_facility/invite_facility', {
      headers,
      params,
    }).toPromise();
    this.invitedFacilitiesSbj.next(data as InvitedFacilityResponse);
  }

  public async resendInvite(payload: InvitedFacility) {
    const headers = this.httpHeaderService.bearerHttpAuthorizationHeader();
    await this.httpClient.post(`${environment.servicePath}/multi_facility/resend_invite_email`, payload, {
      headers,
    }).toPromise();
    return this.invitedFacilities();
  }

  public async removeInvite(facilityId: string) {
    const headers = this.httpHeaderService.bearerHttpAuthorizationHeader();
    let params = new HttpParams();
    params = params.set('requestedFacilityId', facilityId);
    await this.httpClient.delete(`${environment.servicePath}/multi_facility/invite_facility`, {
      headers,
      params,
    }).toPromise();
    return this.invitedFacilities();
  }

  /**
   * Invite Facility
   */
  inviteFacilityByEmail(payload: RequestInviteFacilityModel): Observable<ResponseInviteFacilityModel> {
    const headers = this.httpHeaderService.bearerHttpAuthorizationHeader();
    return this.httpClient.post(`${environment.servicePath}/multi_facility/invite_facility`, payload, {
      headers,
    }) as Observable<ResponseInviteFacilityModel>;
  }

  pendingFacilities() {
    const headers = this.httpHeaderService.bearerHttpAuthorizationHeader();
    return this.httpClient.post(`${environment.servicePath}/multi_facility/invite_facility_pending`, {
      headers,
    }) as Observable<ResponseInviteFacilityModel>;
  }
  updateAcceptRejectRequest(inviteFacilityRequestEntity: AcceptRejectRequestPayload) {
    const headers = this.httpHeaderService.bearerHttpAuthorizationHeader();
    return this.httpClient.post(
      `${environment.servicePath}/multi_facility/update_facility_request`,
      inviteFacilityRequestEntity,
      {
        headers,
      }
    ) as Observable<any>;
  }

  getSelectedFacilities(facilities: MultiFacilityModel[]): string[]{
    let selectedFacilities:string[] = [];
    facilities.forEach((facility: MultiFacilityModel) => {
      if(facility.children.length > 0){
        selectedFacilities = [...this.getSelectedFacilities(facility.children), ...selectedFacilities]
      } else{
        if(facility.isSelected){
          selectedFacilities.push(facility.facilityId);
        }
      }
    });
    return selectedFacilities;
  }

  getFacility(facilityId: string) : MultiFacilityModel | undefined {
    return this.findChildFac(this._multiFacilities, facilityId);
  }

  private findChildFac(facilities: MultiFacilityModel[], facilityId: string) : MultiFacilityModel | undefined {
    if(!facilities) return undefined;
    let fac = facilities.find(f => f.facilityId == facilityId)
    if(!fac) {
      for (let idx = 0; idx < facilities.length; idx++) {
        let f = this.findChildFac(facilities[idx].children, facilityId);
        if(f) return f;
      }
    }
    return fac;
  }

  markSelection(facilities: MultiFacilityModel[], checkedAll: boolean): MultiFacilityModel[]{
    let facilitiesForMarkSelection: MultiFacilityModel[] = facilities;
    facilities.forEach((facilityForMarkSelection: MultiFacilityModel) => {
      if(facilityForMarkSelection.children.length > 0){
        facilityForMarkSelection.isSelected = checkedAll;
        facilityForMarkSelection.children = this.markSelection(facilityForMarkSelection.children, checkedAll);
      } else{
        facilityForMarkSelection.isSelected = checkedAll;
      }
    });
    return facilitiesForMarkSelection;
  }

  markSelectionBaseOnSelectedFacilities(facilities: MultiFacilityModel[], selectedFacList: string[]): MultiFacilityModel[]{
    let facilitiesForMarkSelection: MultiFacilityModel[] = facilities;
    facilities.forEach((facilityForMarkSelection: MultiFacilityModel) => {
      if(facilityForMarkSelection.children.length > 0){
        facilityForMarkSelection.isSelected = selectedFacList.indexOf(facilityForMarkSelection.facilityId) > -1;
        facilityForMarkSelection.children = this.markSelectionBaseOnSelectedFacilities(facilityForMarkSelection.children, selectedFacList);
      } else{
        facilityForMarkSelection.isSelected = selectedFacList.indexOf(facilityForMarkSelection.facilityId) > -1;;
      }
    });
    return facilitiesForMarkSelection;
  }
}
